import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { CollapsedPopover } from "@/components/doc/right-sidebar/CollapsedPopover";
import { TableBordersDropdown } from "@/components/doc/right-sidebar/table-style/TableBordersDropdown";
import { TableDimensions } from "@/components/doc/right-sidebar/table-style/TableDimensions";
import { TableHeaderStyle } from "@/components/doc/right-sidebar/table-style/TableHeaderStyle";
import { TableToggleDisclosure } from "@/components/doc/right-sidebar/table-style/TableToggleDisclosure";
import { Icon } from "@/components/icons/Icon";
import { ActionsDisclosure } from "@/components/sidebar/ActionsDisclosure";
import { useTableEditorState } from "@/components/tiptap/selectors/useTableEditorState";

import type { DocumentModel } from "@/models/DocumentModel";

type Props = {
  doc: DocumentModel;
  isCollapsed?: boolean;
};

const Content = observer(({ doc }: { doc: DocumentModel }) => {
  const t = useTranslations("sidebar.tableStyle");
  const { tableNode, headerColumnIsEnabled, headerRowIsEnabled } = useTableEditorState(doc) ?? {};

  if (!tableNode) {
    return null;
  }

  const handleOnToggleHeaderRowClick = () => {
    doc.editor.tiptap?.commands.toggleHeaderRow();
    doc.focus();
  };

  const handleOnToggleHeaderColumnClick = () => {
    doc.editor.tiptap?.commands.toggleHeaderColumn();
    doc.focus();
  };

  return (
    <>
      <div className="flex items-center">
        <TableDimensions doc={doc} tableNode={tableNode} />
        <TableBordersDropdown doc={doc} className="ml-auto" />
      </div>
      <hr className="mx-1.5 border-gray-300 dark:border-gray-600 mt-1.5 mb-1.25" />
      <TableToggleDisclosure
        key={`row-${tableNode.pos}-${headerRowIsEnabled}`}
        title={t("headerRow.title")}
        checked={!!headerRowIsEnabled}
        onToggleClick={handleOnToggleHeaderRowClick}
      >
        <TableHeaderStyle doc={doc} tableNode={tableNode} type="row" />
      </TableToggleDisclosure>
      <TableToggleDisclosure
        key={`column-${tableNode.pos}-${headerColumnIsEnabled}`}
        title={t("headerColumn.title")}
        checked={headerColumnIsEnabled}
        onToggleClick={handleOnToggleHeaderColumnClick}
      >
        <TableHeaderStyle doc={doc} tableNode={tableNode} type="column" />
      </TableToggleDisclosure>
    </>
  );
});

export const TableStyle = observer(function TableStyle({ doc, isCollapsed }: Props) {
  const t = useTranslations("sidebar.tableStyle");
  const { tableNode } = useTableEditorState(doc) ?? {};

  if (!tableNode) {
    return null;
  }

  if (isCollapsed) {
    return (
      <CollapsedPopover
        icon={<Icon name="TableSettings" />}
        tooltipTitle={t("title")}
        title={t("title")}
      >
        <Content doc={doc} />
      </CollapsedPopover>
    );
  }

  return (
    <ActionsDisclosure
      data-panel="table_style"
      title={t("title")}
      disclosureProps={{ defaultOpen: true }}
      disclosureButtonProps={{ "aria-label": t("panelButtonLabel") }}
    >
      <Content doc={doc} />
    </ActionsDisclosure>
  );
});
